import { UndoService } from '../../..';
import {
  fixColgroup,
  getCellCoordinateInPlan,
  getTablePlan,
  getVerticalAlignedCells,
} from '../../../helpers/dom/table';
import { getEmotion } from '../../../helpers/emotion';
import { MouseTrackingController } from '../../../helpers/fromMouseTracking';

function setupTableForResizing(table: HTMLTableElement) {
  // Create or fix the table's colgroup to set all columns to their proper width
  fixColgroup(table);

  // Tables pasted from excel have fixed width, which conflicts with column resizing.
  table.removeAttribute('width');
  table.style.removeProperty('width');
}

export class ColumnResizeController implements MouseTrackingController {
  private readonly columnCells: HTMLTableCellElement[];
  private readonly col: HTMLTableColElement | null = null;

  constructor(
    table: HTMLTableElement,
    cell: HTMLTableCellElement,
    mouseDownEvent: MouseEvent,
    undoService?: UndoService,
  ) {
    this.columnCells = getVerticalAlignedCells(table, cell).flat();

    setupTableForResizing(table);
    const plan = getTablePlan(table);
    const coordinates = getCellCoordinateInPlan(cell, plan);

    if (coordinates) {
      this.col = table.querySelector(`col:nth-child(${coordinates.column + 1})`);
    }

    const cellRect = cell.getBoundingClientRect();

    // Collect cell metrics, which is a potentially expensive operation.
    this.state = {
      cellRight: cellRect.right,
      cellWidth: cellRect.width,
      mouseX: mouseDownEvent.clientX,
      resumeTakingSnapshots: undoService?.pauseTakingSnapshots(),
    };
  }

  private readonly state: {
    cellRight: number;
    cellWidth: number;
    mouseX: number;
    resumeTakingSnapshots?: () => void;
  };

  changeColumnWidth(width: number) {
    if (this.col) {
      this.col.setAttribute('width', `${width}px`);
    }

    for (const cell of this.columnCells) {
      cell.style.width = `${width}px`;
    }
  }

  // eslint-disable-next-line class-methods-use-this
  onLayer(layer: Element) {
    const { css } = getEmotion(layer.ownerDocument);
    layer.classList.add(css`
      cursor: col-resize;
    `);
  }

  onMouseMove(event: MouseEvent) {
    const { state } = this;

    const displacement = event.clientX - state.mouseX;
    const newWidth = Math.max(0, state.cellWidth + displacement);
    this.changeColumnWidth(newWidth);
  }

  onMouseUp() {
    const { resumeTakingSnapshots } = this.state;
    if (resumeTakingSnapshots) {
      resumeTakingSnapshots();
    }
  }
}
